/**
 * ShortcutHelp.tsx — die Kuerzelhilfe (docs/30 §9.2 / §5.5 Lauf-4 Zeile M).
 *
 * Ein globales ?-Overlay, das die Liste aller Tastenkuerzel DIREKT AUS DER
 * REGISTRY generiert (getRegisteredCommands) — es gibt bewusst KEINE gepflegte
 * Zweitliste, die von den echten Kommandos abdriften koennte. Jede Aktion
 * erscheint genau einmal (Dedupe per cmd.id), gruppiert nach cmd.group.
 *
 * Der Oeffner ist selbst ein Registry-Kommando (useCommandScope 'global'), damit
 * er durch denselben EINEN Dispatcher (HotkeyHost/useHotkeys) laeuft: IME-Guard,
 * Textfeld-Regel und Normalisierung gelten automatisch. '?' wird von normalize()
 * mit gehaltener Shift-Taste zu 'shift+?' (parts: 'shift' + e.key.toLowerCase());
 * manche Layouts liefern nur '?'. Deshalb sind BEIDE KeySpecs registriert.
 *
 * Das Overlay selbst ist ein natives <dialog> (showModal), gegated ueber den
 * zentralen overlayStack — kein eigener window-keydown-Listener. Esc/Hardware-
 * Back schliessen nur, wenn dieses Overlay die oberste Ebene ist; der Fokus
 * kehrt beim Schliessen zum vorher fokussierten Element zurueck.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useT } from '../i18n';
import { pushOverlay, peekTop } from './overlayStack';
import { useCommandScope, getRegisteredCommands, type Command } from './registry';
import type { KeySpec } from './useHotkeys';
import { Icon } from '../components/ui/Icon';
import styles from './ShortcutHelp.module.css';

// ---------------------------------------------------------------------------
// Modifikator-Erkennung — IDENTISCH zu useHotkeys.ts, damit die angezeigte
// Modifikator-Taste (⌘ vs. Strg) zur real matchenden Taste passt.
// ---------------------------------------------------------------------------

const IS_APPLE = typeof navigator !== 'undefined'
  && /Mac|iPad|iPhone/.test(navigator.platform ?? navigator.userAgent);

// KeySpec-Teil → angezeigter Glyph. Tastatur-Glyphen sind KEINE Emoji (erlaubt).
// P26-Verify: Wort-Labels (Strg/Alt/Win) laufen ueber i18n — nur die
// Apple-GLYPHEN (⌘/⌥/⇧/⏎) sind sprachneutral hart.
const MOD_GLYPH = IS_APPLE ? '⌘' : null; // null => t('shortcut.key.ctrl')
const ALT_GLYPH = IS_APPLE ? '⌥' : null; // null => t('shortcut.key.alt')
const SHIFT_GLYPH = '⇧'; // ⇧
const ENTER_GLYPH = '⏎'; // ⏎

/**
 * Formatiert einen einzelnen KeySpec-Teil zu seinem Anzeige-Glyph.
 *
 * - 'mod'   → ⌘ (Apple) bzw. Strg
 * - 'meta'  → ⌘/Win (physische Meta-Taste, wenn NICHT der Kommando-Modifikator)
 * - 'ctrl'  → Strg (physisches Strg, wenn NICHT der Kommando-Modifikator)
 * - 'alt'   → ⌥/Alt
 * - 'shift' → ⇧
 * - 'enter'/'return' → ⏎
 * - 'escape' → Esc
 * - 'KeyI'  → letzter Buchstabe (physische Taste, wird angezeigt als 'I')
 * - sonst   → das Zeichen selbst, Grossbuchstaben (z.B. '0', '\', '?')
 */
export function formatKeyPart(part: string, t: (key: string) => string): string {
  switch (part) {
    case 'mod':
      return MOD_GLYPH ?? t('shortcut.key.ctrl');
    case 'meta':
      return IS_APPLE ? '⌘' : t('shortcut.key.win');
    case 'ctrl':
      return t('shortcut.key.ctrl');
    case 'alt':
      return ALT_GLYPH ?? t('shortcut.key.alt');
    case 'shift':
      return SHIFT_GLYPH;
    case 'enter':
    case 'return':
      return ENTER_GLYPH;
    case 'escape':
      return 'Esc';
    default:
      // Physische Tasten kommen als 'KeyI'/'Digit1'/… → letztes Zeichen nehmen.
      if (/^Key[A-Z]$/.test(part)) return part.slice(-1);
      if (/^Digit[0-9]$/.test(part)) return part.slice(-1);
      // Einzelzeichen ('0', '\\', '?', 'k') → Grossbuchstabe fuer die Anzeige.
      return part.length === 1 ? part.toUpperCase() : part;
  }
}

/** Formatiert einen ganzen KeySpec zu Glyphen, durch Leerzeichen getrennt
 *  (z.B. 'mod+shift+?' → '⌘ ⇧ ?'). */
export function formatKeySpec(spec: KeySpec, t: (key: string) => string): string {
  return spec
    .split('+')
    .map((part) => formatKeyPart(part, t))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Gruppierung
// ---------------------------------------------------------------------------

const DEFAULT_GROUP = 'global';

/** Dedupliziert Kommandos per id (jede Aktion genau einmal) und ordnet sie den
 *  Gruppen zu. Kommandos ohne keys werden ausgelassen (keine Zeile ohne Kuerzel).
 *  Reihenfolge = Registrierungs-Reihenfolge (stabil). */
function groupCommands(cmds: readonly Command[]): { group: string; items: Command[] }[] {
  const seen = new Set<string>();
  const order: string[] = [];
  const byGroup = new Map<string, Command[]>();

  for (const cmd of cmds) {
    if (seen.has(cmd.id)) continue; // Dedupe per id
    if (!cmd.keys || cmd.keys.length === 0) continue;
    seen.add(cmd.id);
    const group = cmd.group ?? DEFAULT_GROUP;
    if (!byGroup.has(group)) {
      byGroup.set(group, []);
      order.push(group);
    }
    byGroup.get(group)!.push(cmd);
  }

  return order.map((group) => ({ group, items: byGroup.get(group)! }));
}

// ---------------------------------------------------------------------------
// Overlay-Komponente
// ---------------------------------------------------------------------------

interface ShortcutHelpDialogProps {
  onClose: () => void;
}

function ShortcutHelpDialog({ onClose }: ShortcutHelpDialogProps) {
  const { t } = useT();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const overlayId = useId();

  // Stabiles onClose fuer den overlayStack-Effekt (kein Re-Register pro Render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Natives <dialog> oeffnen (showModal) + Fokus merken/zuruecksetzen.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    // showModal()/close() sind in jsdom (Test) nicht implementiert — dann als
    // Fallback das open-Attribut setzen, damit das <dialog> abfragbar bleibt.
    // Im Browser ist showModal der echte modale Pfad (Top-Layer).
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) {
        try { dialog.showModal(); } catch { dialog.open = true; }
      }
    } else {
      dialog.open = true;
    }
    return () => {
      // dialog aus dem Effekt-Setup wiederverwenden — kein erneutes
      // dialogRef.current-Lesen im Cleanup (react-hooks/exhaustive-deps).
      if (dialog) {
        if (typeof dialog.close === 'function' && dialog.open) {
          try { dialog.close(); } catch { dialog.open = false; }
        } else {
          dialog.open = false;
        }
      }
      const focusTarget = restoreFocusRef.current;
      if (focusTarget && document.contains(focusTarget)) {
        focusTarget.focus({ preventScroll: true });
      }
    };
  }, []);

  // Merkt die eigene overlayStack-Identitaet, damit der onCancel-Prop-Handler
  // (unten) peekTop() gegen genau diese Ebene pruefen kann.
  const entryRef = useRef<{ id: string; close: () => void } | null>(null);

  // Zentrale LIFO-Overlay-Verwaltung: Esc / Hardware-Back schliessen genau diese
  // Ebene (peekTop-gegated). KEIN eigener window-keydown-Listener — Esc laeuft
  // ueber das native <dialog> (cancel-Event, siehe onCancel-Prop im Render).
  useEffect(() => {
    const entry = { id: overlayId, close: () => onCloseRef.current() };
    entryRef.current = entry;
    const unregister = pushOverlay(entry);
    return () => {
      unregister();
      entryRef.current = null;
    };
  }, [overlayId]);

  // Das native <dialog> feuert bei Esc ein 'cancel'-Event und schliesst sich
  // selbst — wir verhindern das Default-Schliessen und spiegeln es in den
  // React-State, aber nur wenn dieses Overlay die OBERSTE Ebene ist.
  const handleCancel = useCallback((event: React.SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    if (peekTop() !== entryRef.current) return;
    onCloseRef.current();
  }, []);

  const groups = groupCommands(getRegisteredCommands());
  const isEmpty = groups.length === 0;

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    // Klick auf das <dialog> selbst (Backdrop-Bereich), nicht auf den Inhalt.
    if (event.target === dialogRef.current) onCloseRef.current();
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby={`${overlayId}-title`}
      onClick={handleBackdropClick}
      onCancel={handleCancel}
      data-testid="shortcut-help"
    >
      <div className={styles.panel}>
        <header className={styles.header}>
          <h2 className={styles.title} id={`${overlayId}-title`}>
            {t('shortcut.title')}
          </h2>
          <button
            type="button"
            className={styles.close}
            onClick={() => onCloseRef.current()}
            aria-label={t('common.close')}
            data-autofocus
          >
            <Icon name="close" />
          </button>
        </header>

        <div className={styles.body}>
          {isEmpty ? (
            <p className={styles.empty}>{t('shortcut.empty')}</p>
          ) : (
            groups.map(({ group, items }) => (
              <section key={group} className={styles.group} data-group={group}>
                <h3 className={styles.groupTitle}>{t(`shortcut.group.${group}`)}</h3>
                <ul className={styles.list}>
                  {items.map((cmd) => (
                    <li key={cmd.id} className={styles.row} data-testid={`shortcut-row-${cmd.id}`}>
                      <span className={styles.rowLabel}>{t(cmd.titleKey)}</span>
                      <span className={styles.rowKeys}>
                        {/* Mehrere Alternativen (z.B. 'shift+?' UND '?') nebeneinander;
                            der Abstand kommt aus der CSS (.rowKeys gap). */}
                        {(cmd.keys ?? []).map((spec) => (
                          <kbd key={spec} className={styles.kbd}>
                            {formatKeySpec(spec, t)}
                          </kbd>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Oeffentliche Komponente: registriert den Oeffner + haelt den offen-State.
// ---------------------------------------------------------------------------

/**
 * ShortcutHelp — global montiert (einmal im App-Baum). Registriert das
 * ?-Kommando in der Registry und zeigt bei Ausloesung das Overlay.
 */
export function ShortcutHelp(): JSX.Element | null {
  const [open, setOpen] = useState(false);

  const openHelp = useCallback(() => setOpen(true), []);
  const closeHelp = useCallback(() => setOpen(false), []);

  // Der Oeffner laeuft ueber die Registry → EIN Dispatcher, IME-/Textfeld-Regeln
  // gelten automatisch. 'shift+?' UND '?' registrieren (Layout-Varianten).
  useCommandScope('global', [
    {
      id: 'help.shortcuts',
      titleKey: 'shortcut.title',
      keys: ['shift+?', '?'],
      group: 'global',
      run: openHelp,
    },
  ]);

  if (!open) return null;
  return <ShortcutHelpDialog onClose={closeHelp} />;
}
